'use client';

import * as React from 'react';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Unstable_Grid2';
import { CustomerInfo, DateFormats, GetParcelSearchParameters } from '@lanterds/types';
import dayjs, { Dayjs } from 'dayjs';
import { queryParamsEnabled } from 'src/util/featureFlags';
import { extractUserSearchInput, getUserEmailFromToken } from 'src/util/helpers';
import ParcelSearchErrors from '../formErrors';
import { ParcelSearchContext } from '../ParcelSearchContext';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { useSnackbar } from 'notistack';
import { Autocomplete, TextField, Tooltip } from '@mui/material';
import { ChangeEvent, FormEvent, SyntheticEvent, useContext, useEffect, useState } from 'react';
import LoadingIndicator from 'src/components/common/LoadingIndicator/LoadingIndicator';
import './ParcelSearchForm.scss';
import { SnackbarVariantNames } from 'src/util/types';
import { isAuthorized } from 'src/util/Permissions';
import { Permissions } from '@lanterds/types';

interface FieldErrors {
    startDateError?: string;
    endDateError?: string;
    parcelNumberError?: string;
    manifestNumberError?: string;
}

export const defaultParcelSearchFormState: Partial<GetParcelSearchParameters> = {
    startDate: '',
    endDate: '',
    customerId: undefined,
    parcelNumber: '',
    endPointNumber: '',
    manifestNumber: '',
    partNumber: '',
};

export default function ParcelSearchForm({ shouldCondense = false }: { shouldCondense?: boolean }) {
    const loggedInUserEmail = getUserEmailFromToken();
    const extendedSearchAuthorized = isAuthorized(loggedInUserEmail, Permissions.parcelSearch_extendedSearch);

    const {
        shouldShowLoading,
        customers,
        customersAreLoading,
        fetchCustomers,
        formError,
        setFormError,
        searchParams,
        handleReset: resetHandler,
        handleSubmit: submitHandler,
        snackbarKeys,
        setSnackbarKeys,
        closeSnackbars,
    } = useContext(ParcelSearchContext);

    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [formData, setFormData] = useState<Partial<GetParcelSearchParameters>>(defaultParcelSearchFormState);
    const [hasReset, setHasReset] = useState(false);
    const [customerName, setCustomerName] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState<{ label: string; id: string } | null>(null);
    const [hasSetInputs, setHasSetInputs] = useState(false);

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    // NOTE: persists form input on load
    useEffect(() => {
        if (customers.length > 0 && !hasSetInputs) {
            // NOTE: we are only concerned about input parameters in this scenario
            const tmpParams = extractUserSearchInput(searchParams);
            const hasPersistedFieldsInContext = Object.keys(tmpParams).length > 0;
            let persistedInputData: Partial<GetParcelSearchParameters> = hasPersistedFieldsInContext ? searchParams : {};

            // NOTE: prefer context values if they are present
            if (queryParamsEnabled && !hasPersistedFieldsInContext) {
                const queryParams = Object.fromEntries(new URLSearchParams(window.location.search));
                persistedInputData = queryParams;
            }

            if (persistedInputData?.customerId) {
                const customer = customers.find(
                    (customer: CustomerInfo) => customer.customerId === persistedInputData.customerId?.toString(),
                );
                if (customer?.customerName) setCustomerName(customer.customerName);
                if (customer) setSelectedCustomer({ label: customer.customerName, id: customer.customerId });
            }

            setFormData({ ...persistedInputData });

            setHasSetInputs(true);
        }
    }, [searchParams, hasSetInputs, customers]);

    useEffect(() => {
        fetchCustomers();
    }, [customers, fetchCustomers]);

    const handleReset = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        (event.currentTarget as HTMLFormElement).reset();

        setFieldErrors({});

        setHasReset(!hasReset);
        setFormData(defaultParcelSearchFormState);
        setCustomerName('');
        setSelectedCustomer(null);
        setSnackbarKeys({});
        resetHandler();
        closeSnackbar();
    };

    const validateForm = () => {
        const { startDate, endDate, parcelNumber, manifestNumber } = formData;
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const lastDay = dayjs().subtract(7, 'day');
        let isValid = true;
        const newFieldErrors: FieldErrors = {};

        const commonFieldValidations = () => {
            if (!endDate && dayjs(startDate).isBefore(lastDay)) {
                newFieldErrors.startDateError = ParcelSearchErrors.noEndDate;
            }

            if (parcelNumber && parcelNumber.length < 4) {
                newFieldErrors.parcelNumberError = ParcelSearchErrors.requiredLength;
            }

            if (startDate && endDate && start > end) {
                newFieldErrors.startDateError = ParcelSearchErrors.invalidStartDate;
            }
        };

        if (!extendedSearchAuthorized) {
            if (!parcelNumber && !manifestNumber) {
                newFieldErrors.parcelNumberError = ParcelSearchErrors.parcelOrManifestNum;
                newFieldErrors.manifestNumberError = ParcelSearchErrors.parcelOrManifestNum;
            } else {
                if (parcelNumber && parcelNumber.includes('%')) {
                    newFieldErrors.parcelNumberError = ParcelSearchErrors.invalidCharacter;
                }

                if (manifestNumber) {
                    if (manifestNumber.length < 4) {
                        newFieldErrors.manifestNumberError = ParcelSearchErrors.requiredLength;
                    }
                }

                commonFieldValidations();
            }
        } else {
            if ((!parcelNumber || parcelNumber.includes('%')) && !startDate) {
                if (!parcelNumber) {
                    newFieldErrors.parcelNumberError = ParcelSearchErrors.startDateOrParcelNum;
                }

                if (!startDate) {
                    newFieldErrors.startDateError = ParcelSearchErrors.startDateOrParcelNum;
                }
            } else {
                commonFieldValidations();
            }
        }

        if (Object.keys(newFieldErrors).length) {
            setFieldErrors(newFieldErrors);
            isValid = false;
        }

        if (isValid) {
            setFormError('');
            setFieldErrors({});
        }

        return isValid;
    };

    useEffect(() => {
        if (formError) {
            const snackbarKey = enqueueSnackbar(formError, {
                variant: 'error',
                autoHideDuration: null,
                preventDuplicate: true,
                action: (key) => (
                    <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });

            if (!snackbarKeys) {
                setSnackbarKeys({ error: [snackbarKey] });
            } else {
                const errorSnackbarKeys = snackbarKeys.error;
                if (errorSnackbarKeys) {
                    for (const key of errorSnackbarKeys) {
                        closeSnackbar(key);
                    }
                }
                setSnackbarKeys({ ...snackbarKeys, error: [snackbarKey] });
            }

            setFieldErrors({});
        }
    }, [formError, shouldShowLoading]);

    useEffect(() => {
        if (Object.keys(fieldErrors).length) {
            setFormError(null);
            if (snackbarKeys && snackbarKeys.error) {
                closeSnackbars(SnackbarVariantNames.Error, snackbarKeys.error);
            }
        }
    }, [fieldErrors, snackbarKeys]);

    const handleSubmit = (event: SyntheticEvent) => {
        event.preventDefault();

        const formIsValid = validateForm();

        setCustomerName(customerName);
        setSelectedCustomer(selectedCustomer);
        if (formIsValid) {
            submitHandler(formData);
            closeSnackbar();
        }
    };

    const handleDatePickerChange = (name: string, value: Dayjs | null) => {
        setFormData({
            ...formData,
            [name]: value?.format(DateFormats.IsoDate),
        });
    };

    const handleCustomerChange = (event: SyntheticEvent<Element, Event>, value: { label: string; id: string } | null) => {
        setCustomerName(value?.id ?? '');
        setSelectedCustomer(value?.label && value?.id ? { label: value.label, id: value.id } : null);

        if (value && value?.id !== '') {
            const customer = customers.find(
                (customer: CustomerInfo) => customer.customerId.toLocaleLowerCase() === value.id.toLocaleLowerCase(),
            );

            if (customer && customer?.customerId) {
                setFormData({
                    ...formData,
                    customerId: Number(customer.customerId) ? Number(customer.customerId) : customer.customerId,
                });
            }
        } else {
            setFormData({
                ...formData,
                customerId: undefined,
            });
        }
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleCustomerIdReset = () => {
        setCustomerName('');
        setSelectedCustomer(null);
        setFormData({
            ...formData,
            customerId: undefined,
        });
    };

    const getFieldWidth = (divisor: number) => {
        return 12 / divisor;
    };

    return (
        <form onReset={handleReset} onSubmit={handleSubmit} autoComplete="off">
            <Card id="parcel-search-form">
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid
                            xl={getFieldWidth(shouldCondense ? 8 : 4)}
                            lg={getFieldWidth(shouldCondense ? 4 : 4)}
                            md={getFieldWidth(shouldCondense ? 7 : 4)}
                            sm={shouldCondense ? 6 : 4}
                            xs={12}
                        >
                            <FormControl fullWidth>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    {/* DatePicker is a special case where we define the label in the slotProps and not as an InputLabel */}
                                    <DatePicker
                                        desktopModeMediaQuery="@media (min-width: 400px)"
                                        name="startDate"
                                        value={formData.startDate ? dayjs(formData.startDate) : null}
                                        onChange={(value) => handleDatePickerChange('startDate', value)}
                                        slotProps={{
                                            field: { clearable: true },
                                            actionBar: { actions: ['clear', 'today'], sx: { justifyContent: 'space-between' } },
                                            textField: {
                                                id: 'startDate',
                                                variant: 'outlined',
                                                placeholder: 'MM/DD/YYYY',
                                                label: 'Start Date',
                                                error: !!fieldErrors?.startDateError,
                                                helperText: fieldErrors?.startDateError,
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid>
                        <Grid
                            xl={getFieldWidth(shouldCondense ? 8 : 4)}
                            lg={getFieldWidth(shouldCondense ? 4 : 4)}
                            md={getFieldWidth(shouldCondense ? 7 : 4)}
                            sm={shouldCondense ? 6 : 4}
                            xs={12}
                        >
                            <FormControl fullWidth>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    {/* DatePicker is a special case where we define the label in the slotProps and not as an InputLabel */}
                                    <DatePicker
                                        desktopModeMediaQuery="@media (min-width: 400px)"
                                        name="endDate"
                                        value={formData.endDate ? dayjs(formData.endDate) : null}
                                        onChange={(value) => handleDatePickerChange('endDate', value)}
                                        slotProps={{
                                            field: { clearable: true },
                                            actionBar: { actions: ['clear', 'today'], sx: { justifyContent: 'space-between' } },
                                            textField: {
                                                id: 'endDate',
                                                variant: 'outlined',
                                                placeholder: 'MM/DD/YYYY',
                                                label: 'End Date',
                                                error: !!fieldErrors?.endDateError,
                                                helperText: fieldErrors?.endDateError,
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid>
                        {extendedSearchAuthorized && (
                            <Grid
                                xl={getFieldWidth(shouldCondense ? 8 : 4)}
                                lg={getFieldWidth(shouldCondense ? 4 : 4)}
                                md={getFieldWidth(shouldCondense ? 7 : 4)}
                                sm={shouldCondense ? 6 : 4}
                                xs={12}
                            >
                                <FormControl fullWidth>
                                    <Autocomplete
                                        id="customerId"
                                        disablePortal
                                        options={customers.map((customer) => ({
                                            label: customer.customerName,
                                            id: customer.customerId,
                                        }))}
                                        isOptionEqualToValue={(
                                            option: {
                                                label: string;
                                                id: string;
                                            },
                                            value: {
                                                label: string;
                                                id: string;
                                            },
                                        ) => option.id === value.id}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                label="Customer"
                                                name="customerId"
                                                InputProps={{
                                                    ...params.InputProps,
                                                }}
                                                InputLabelProps={{ ...params.InputLabelProps }}
                                            />
                                        )}
                                        value={selectedCustomer ?? null}
                                        onChange={handleCustomerChange}
                                        disabled={customersAreLoading}
                                        onReset={handleCustomerIdReset}
                                    />
                                </FormControl>
                            </Grid>
                        )}

                        <Grid
                            xl={getFieldWidth(shouldCondense ? 8 : 4)}
                            lg={getFieldWidth(shouldCondense ? 4 : 4)}
                            md={getFieldWidth(shouldCondense ? 7 : 4)}
                            sm={shouldCondense ? 6 : 4}
                            xs={12}
                        >
                            <FormControl fullWidth>
                                <Tooltip
                                    arrow
                                    title={
                                        extendedSearchAuthorized ? 'Use the percent (%) character as a search wild card' : ''
                                    }
                                    placement="top"
                                >
                                    <TextField
                                        id="parcelNumber"
                                        variant="outlined"
                                        label="Parcel Number"
                                        name="parcelNumber"
                                        value={formData.parcelNumber ?? ''}
                                        onChange={handleInputChange}
                                        InputProps={{
                                            sx: { borderRadius: '10px' },
                                        }}
                                        error={!!fieldErrors?.parcelNumberError}
                                        helperText={fieldErrors?.parcelNumberError}
                                    />
                                </Tooltip>
                            </FormControl>
                        </Grid>
                        {extendedSearchAuthorized && (
                            <Grid
                                xl={getFieldWidth(shouldCondense ? 8 : 4)}
                                lg={getFieldWidth(shouldCondense ? 4 : 4)}
                                md={getFieldWidth(shouldCondense ? 7 : 4)}
                                sm={shouldCondense ? 6 : 4}
                                xs={12}
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        id="endPointNumber"
                                        variant="outlined"
                                        label="End Point Number"
                                        name="endPointNumber"
                                        value={formData.endPointNumber ?? ''}
                                        onChange={handleInputChange}
                                    />
                                </FormControl>
                            </Grid>
                        )}
                        <Grid
                            xl={getFieldWidth(shouldCondense ? 8 : 4)}
                            lg={getFieldWidth(shouldCondense ? 4 : 4)}
                            md={getFieldWidth(shouldCondense ? 7 : 4)}
                            sm={shouldCondense ? 6 : 4}
                            xs={12}
                        >
                            <FormControl fullWidth>
                                <TextField
                                    id="manifestNumber"
                                    variant="outlined"
                                    label="Manifest Number"
                                    name="manifestNumber"
                                    value={formData.manifestNumber ?? ''}
                                    onChange={handleInputChange}
                                    error={!!fieldErrors?.manifestNumberError}
                                    helperText={fieldErrors?.manifestNumberError}
                                />
                            </FormControl>
                        </Grid>
                        {extendedSearchAuthorized && (
                            <Grid
                                xl={getFieldWidth(shouldCondense ? 8 : 4)}
                                lg={getFieldWidth(shouldCondense ? 4 : 4)}
                                md={getFieldWidth(shouldCondense ? 7 : 4)}
                                sm={shouldCondense ? 6 : 4}
                                xs={12}
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        id="partNumber"
                                        variant="outlined"
                                        label="Part Number"
                                        name="partNumber"
                                        value={formData.partNumber ?? ''}
                                        onChange={handleInputChange}
                                    />
                                </FormControl>
                            </Grid>
                        )}
                        <Grid
                            xl={extendedSearchAuthorized ? getFieldWidth(shouldCondense ? 8 : 4) : 12}
                            lg={extendedSearchAuthorized ? getFieldWidth(shouldCondense ? 4 : 4) : 12}
                            md={extendedSearchAuthorized ? getFieldWidth(shouldCondense ? 1 : 4) : 12}
                            sm={extendedSearchAuthorized ? (shouldCondense ? 6 : 8) : 12}
                            xs={12}
                            className="buttons"
                        >
                            <CardActions>
                                <Button size="small" variant="outlined" type="reset" className="clear-button">
                                    Clear Form
                                </Button>
                                {shouldShowLoading ? (
                                    <Button size="medium" variant="contained" type="submit" disabled className="submit-button">
                                        <LoadingIndicator message="Loading" />
                                    </Button>
                                ) : (
                                    <Button size="medium" variant="contained" type="submit" className="submit-button">
                                        Search
                                    </Button>
                                )}
                            </CardActions>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </form>
    );
}
